import type { ColorPalette, ThemeHeadingPalette, ThemePalette, ThemeTypePalette, FontPalette, ThemeChartPalette, AssetPalette, GradientPalette, ShadowPalette, RadialPalette, } from "@bracket/ui/types";
import type { CSSProperties, } from "react";


export const basePalette: ColorPalette = {
	palette_type: "light",
	white: "#FFFFFF",
	black: "#000000",
	surface: "#eff1ef",
	surface_on: "#101510",
	paragraph: "#101010",
	subheading: "#202020",
	heading: "#101010",
	title: "#767978",
	background: "#F5F5F5",
	background_on: "#101510",
	primary: "#01512E",
	primary_variant: "#00542F",
	surface_container: "#eff1ef",
	surface_container_on: "#101510",
	primary_on: "#FDFDFD",
}

export const palette: ThemePalette = {
	...basePalette,

	primary_container: "#1E6043",
	primary_container_on: "#B2F1C0",
	primary_fixed: "#B2F1C0",
	primary_fixed_dim: "#97D5A5",


	outline: "#8B938A",
	outline_variant: "#c1c1c1",

	surface_hover: "#e6e8e8",

	surface_variant: "#D0DBD6",
	surface_variant_on: "#222222",

	surface_inverse: basePalette.surface_on,
	surface_inverse_on: basePalette.surface,
	surface_light: basePalette.surface,
	surface_light_on: basePalette.surface_on,

	surface_dark: basePalette.surface_on,
	surface_dark_on: basePalette.surface,

	surface_container_lowest: "#FFFFFF",
	surface_container_low: "#e6e8e8",
	surface_container_high: "#F0F0F0",
	surface_container_highest: "#FFFFFF",
	surface_container_bright: "#FFFFFF",
	surface_container_dim: "#a1a1a1",

	success: "#00542F",
	success_on: "#00542F",
	success_container: "#57FF0320",

	error_on: "#E42F0380",
	error: "#E42F03",
	error_container: "#E42F0320",

	warning_on: "#FBBC2C80",
	warning: "#FBBC2C",
	warning_container: "#FBBC2C20",

	debug_on: "#FF000080",
	debug: "#F00",

	accent_blue: "#AFC6FF",
	accent_teal: "#A2CED9",
	accent_orange: "#FFB87C",
	accent_pink: "#FF7CA3",
	accent_purple: "#B890FF",
	accent_brand: "#03FF8F",
	swatches: "swatch_one",
	swatch_one: [
		"#00542F",
		"#548771",
		"#A6B9B1",
		"#B8C5BF",
		"#C0C9C5",
	],
	tint_lightest: "#00000014",
	tint_lighter: "#00000014",
	tint_light: "#FFFFFF28",
	tine: "#00000014",
	tint_dark: "#00000014",
	tint_darker: "#00000014",
	tint_darkest: "#00000014",

} as const;


export const fonts: FontPalette = {
	numeric: "Space Mono",
	body: "Inter",
	heading: "Zalando Sans SemiExpanded",
	heading_variant: "Zalando Sans SemiExpanded",
} as const;


export const assets: AssetPalette = {
	menu_background: "/assets/light/nav-menu-bkg.png",
	invest_background: "/assets/bracket-bg-invest.png",
	background_one: "/assets/stakeBg.png",
	HERO_BG: "/assets/bracket-bg-invest.png",
	TX_BKG_ONE: "/assets/light/tx-glass-1.png",
	TX_GRAIN_ONE: "/assets/light/tx-grain-1.png",
} as const;


export const chart: ThemeChartPalette = {
	stroke: palette.surface_variant,
	stroke_width: 10,
	axis: {
		color: palette.outline,
		swatch: "swatch_one",
	}
} as const;

export const radials: RadialPalette = {
	xxsm: "3px", // 2 is to small to even notice
	xsm: "4px",
	sm: "8px",
	md: "16px",
	lg: "24px",
	xl: "32px",
	xxl: "64px",
} as const;

export const gradients: GradientPalette = {
	primary: "linear-gradient(180deg, #C6CBC6 0%, #C6CBC6 100%);",
	secondary: "linear-gradient(180deg, #00542F00 0%, #00542F33 100%)",
	tertiary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	quaternary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	quinary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	senary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	navpanel: "linear-gradient(180deg, #00542F 0%, #02331E 100%)",
} as const;


export const paragraph: ThemeTypePalette = {
	xxsm: {
		font: fonts.body,
		size: "10px",
		lineHeight: "12px",
		weight: "400",
		color: palette.surface_on,
	},	
	xsm: {
		font: fonts.body,
		size: "12px",
		lineHeight: "16px",
		weight: "400",
		color: palette.surface_on,
	},
	sm: {
		font: fonts.body,
		size: "14px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	md: {
		font: fonts.body,
		size: "16px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	lg: {
		font: fonts.body,
		size: "18px",
		lineHeight: "24px",
		weight: "400",
		color: palette.surface_on,
	},
	xl: {
		font: fonts.body,
		size: "20px",
		lineHeight: "28px",
		weight: "400",
		color: palette.surface_on,
	},
	xxl: {
		font: fonts.body,
		size: "24px",
		lineHeight: "32px",
		weight: "400",
		color: palette.surface_on,
	},
} as const;


export const heading: ThemeHeadingPalette = {
	h1: {
		font: fonts.heading,
		size: "60px",
		lineHeight: "80px",
		weight: "500",
		color: palette.surface_on,
	},
	h2: {
		font: fonts.heading,
		size: "48px",
		lineHeight: "60px",
		weight: "500",
		color: palette.surface_on,
	},
	h3: {
		font: fonts.heading,
		size: "40px",
		lineHeight: "52px",
		weight: "500",
		color: palette.surface_on,
	},
	h4: {
		font: fonts.heading,
		size: "36px",
		lineHeight: "48px",
		weight: "500",
		color: palette.surface_on,
	},
	h5: {
		font: fonts.heading,
		size: "24px",
		lineHeight: "32px",
		weight: "500",
		color: palette.surface_on,
	},
	h6: {
		font: fonts.heading_variant,
		size: "20px",
		lineHeight: "26px",
		weight: "500",
		color: palette.surface_on,
	},
} as const;


export const numeric: ThemeTypePalette = {
	xxsm: {
		font: fonts.numeric,
		size: "10px",
		lineHeight: "12px",
		weight: "400",
		color: palette.surface_on,
	},	
	xsm: {
		font: fonts.numeric,
		size: "12px",
		lineHeight: "16px",
		weight: "400",
		color: palette.surface_on,
	},
	sm: {
		font: fonts.numeric,
		size: "14px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	md: {
		font: fonts.numeric,
		size: "16px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	lg: {
		font: fonts.numeric,
		size: "18px",
		lineHeight: "24px",
		weight: "400",
		color: palette.surface_on,
	},
	xl: {
		font: fonts.numeric,
		size: "20px",
		lineHeight: "28px",
		weight: "400",
		color: palette.surface_on,
	},
	xxl: {
		font: fonts.numeric,
		size: "24px",
		lineHeight: "32px",
		weight: "400",
		color: palette.surface_on,
	},
} as const;


export const uniques: Record<string, CSSProperties> = {
	"Special-Strategy-Title": {
		color: palette.black,
		fontWeight: "600",
		lineHeight: "3rem",
	},
};

export const shadows: ShadowPalette = {
	primary: "0px 0px 10px 0px rgba(0, 0, 0, 0.15)",
	secondary: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
	tertiary: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
	quaternary: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
	quinary: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
	senary: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
	navpanel: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
} as const;
